// hooks/useDefaultLocationFromClockIn.js
import { useCallback, useRef, useState } from "react";
import { resetTime } from "../../../util/common";

/**
 * Resolve the default location based on today's latest "clock in" record.
 *
 * @param {object} params
 * @param {string|null} params.companyId
 * @param {string|null} params.employeeId
 * @param {(payload: any) => Promise<any>} params.getClockInOutHandler
 *
 * @returns {{
 *   resolveDefaultLocationFromClockIn: (optionsNow: Array<{value: string}>) => Promise<any|null>,
 *   loading: boolean,
 *   error: any
 * }}
 */
export default function useDefaultLocationFromClockIn({ companyId, employeeId, getClockInOutHandler }) {
  console.log("useDefaultLocationFromClockIn: ", companyId, employeeId, getClockInOutHandler);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // small cache to avoid multiple calls in same session/day
  const cacheRef = useRef({
    dayKey: null,
    locationId: null,
  });

  console.log("useDefaultLocationFromClockIn: ", companyId, employeeId);
  const resolveDefaultLocationFromClockIn = useCallback(    
    async (optionsNow = []) => {
      setError(null);

      if (!companyId || !employeeId) return null;

      const start = resetTime(new Date());
      const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
      const dayKey = start.toISOString().slice(0, 10); // YYYY-MM-DD

      console.log("useDefaultLocationFromClockIn async call: ",
        cacheRef.current.dayKey, 
        cacheRef.current.locationId,
        dayKey
      );
      // cache hit
      if (cacheRef.current.dayKey === dayKey && cacheRef.current.locationId) {
        const cached = optionsNow.find((o) => String(o.value) === String(cacheRef.current.locationId)) || null;
        return cached;
      }

      setLoading(true);
      try {


      console.log("useDefaultLocationFromClockIn getClockInOutHandler: ",
        companyId,
        employeeId, // ok to pass; you still filter below for safety
        start,
        end,
      );

        const resp = await getClockInOutHandler({
          companyId,
          employeeId, // ok to pass; you still filter below for safety
          timeMin: start,
          timeMax: end,
          page: 0,
          size: 100000,
        });

        const items = resp?.data?.items || [];
        const list = Array.isArray(items) ? items : [];

        const myRecords = list.filter((r) => String(r?.employee?.id || "") === String(employeeId));
        if (myRecords.length === 0) return null;

        const latest = myRecords
          .slice()
          .sort((a, b) => {
            const ta = new Date(a?.clockedIn || a?.created || 0).getTime();
            const tb = new Date(b?.clockedIn || b?.created || 0).getTime();
            return tb - ta;
          })[0];

        const locId = latest?.location?.id;
        if (!locId) return null;

        // save in cache
        cacheRef.current.dayKey = dayKey;
        cacheRef.current.locationId = String(locId);

        return optionsNow.find((o) => String(o.value) === String(locId)) || null;
      } catch (e) {
        console.error("resolveDefaultLocationFromClockIn", e);
        setError(e);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [companyId, employeeId, getClockInOutHandler]
  );

  return { resolveDefaultLocationFromClockIn, loading, error };
}